import React from 'react';
import {
  LayoutDashboard,
  Building2,
  Ticket,
  FolderKanban,
  ClipboardCheck,
  Calculator,
  FileText,
  CheckCircle2,
  ShoppingCart,
  Truck,
  Boxes,
  Hammer,
  Award,
  Receipt,
  Layers,
  BookOpen,
  CreditCard,
  Wallet,
  BarChart3,
  FolderArchive,
  TrendingUp,
  History,
  Settings,
  SlidersHorizontal,
  Users,
  ChevronRight,
  Shield,
  Mail,
  Camera,
  CheckCheck,
  MapPin,
  ActivitySquare,
  Clock,
  Smartphone,
} from 'lucide-react';
import { useModules } from '../../context/ModuleContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';

interface SidebarProps {
  currentRoute: string;
  onNavigate: (route: string) => void;
  isOpen: boolean;
  onCloseMobile: () => void;
}

interface NavMenuItem {
  code: string;
  name: string;
  icon: any;
  route: string;
  badge?: string;
  badgeColor?: string;
}

interface NavSection {
  title: string;
  items: NavMenuItem[];
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentRoute,
  onNavigate,
  isOpen,
  onCloseMobile,
}) => {
  const { isModuleActive } = useModules();
  const { hasPermission, currentRole } = useAuth();

  const sections: NavSection[] = [
    {
      title: 'HERE4U CENTER',
      items: [
        { code: 'here4u_center', name: 'HERE4U Command', icon: ActivitySquare, route: 'here4u', badge: 'Hub', badgeColor: 'bg-amber-400 text-slate-900' },
        { code: 'mobile_job', name: 'Mobile Field App', icon: Smartphone, route: 'mobile-job', badge: 'Field', badgeColor: 'bg-amber-400 text-slate-950' },
        { code: 'gmail_center', name: 'Gmail Center', icon: Mail, route: 'gmail', badge: 'Live' },
        { code: 'temporary_cases', name: 'Temporary Cases', icon: Clock, route: 'temp-cases', badge: 'TKT' },
        { code: 'work_orders', name: 'Work Orders', icon: Hammer, route: 'work-orders' },
        { code: 'staff_teams', name: 'Staff & Teams', icon: Users, route: 'staff' },
        { code: 'field_attendance', name: 'GPS Attendance', icon: MapPin, route: 'attendance', badge: 'GPS' },
        { code: 'job_verification', name: 'Job Verification', icon: CheckCheck, route: 'verification' },
        { code: 'work_evidence', name: 'Work Evidence', icon: Camera, route: 'evidence' },
        { code: 'estimator_templates', name: 'Estimator Engine', icon: Calculator, route: 'estimator-templates', badge: '9 Types' },
      ],
    },
    {
      title: 'OPERATIONS',
      items: [
        { code: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, route: 'dashboard' },
        { code: 'ubl_customers', name: 'UBL Branches', icon: Building2, route: 'branches', badge: 'UBL' },
        { code: 'tickets', name: 'Tickets', icon: Ticket, route: 'tickets' },
        { code: 'projects', name: 'Projects', icon: FolderKanban, route: 'projects' },
        { code: 'site_survey', name: 'Site Survey', icon: ClipboardCheck, route: 'surveys' },
        { code: 'boq', name: 'BOQ & Estimation', icon: Calculator, route: 'boqs' },
        { code: 'quotations', name: 'Quotations', icon: FileText, route: 'quotations' },
        { code: 'site_work', name: 'Site Work & Progress', icon: Hammer, route: 'sitework' },
        { code: 'completion_certificates', name: 'Completion Certs', icon: Award, route: 'completion' },
      ],
    },
    {
      title: 'PROCUREMENT',
      items: [
        { code: 'vendors', name: 'Vendors', icon: Truck, route: 'vendors' },
        { code: 'purchase', name: 'Purchase Orders', icon: ShoppingCart, route: 'purchase' },
        { code: 'inventory', name: 'Inventory & Issue', icon: Boxes, route: 'inventory' },
      ],
    },
    {
      title: 'FINANCE & ACCOUNTS',
      items: [
        { code: 'invoices', name: 'Invoices', icon: Receipt, route: 'invoices' },
        {
          code: 'consolidated_invoices',
          name: 'Consolidated Invoices',
          icon: Layers,
          route: 'consolidated-invoices',
          badge: 'Batch',
          badgeColor: 'bg-amber-400 text-slate-900',
        },
        { code: 'accounts', name: 'Accounts & Ledger', icon: BookOpen, route: 'accounts' },
        { code: 'payments', name: 'Payments', icon: CreditCard, route: 'payments' },
        { code: 'expenses', name: 'Expenses', icon: Wallet, route: 'expenses' },
        { code: 'project_costing', name: 'Project Costing', icon: BarChart3, route: 'costing' },
      ],
    },
    {
      title: 'SYSTEM & ADMINISTRATION',
      items: [
        { code: 'documents', name: 'Documents', icon: FolderArchive, route: 'documents' },
        { code: 'reports', name: 'Reports', icon: TrendingUp, route: 'reports' },
        { code: 'audit_logs', name: 'Audit Trail', icon: History, route: 'audit' },
        {
          code: 'module_manager',
          name: 'Module Manager',
          icon: SlidersHorizontal,
          route: 'modules',
          badge: 'Admin',
        },
      ],
    },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          onClick={onCloseMobile}
          className="fixed inset-0 bg-slate-900/50 z-30 lg:hidden"
        />
      )}

      <aside
        className={`app-sidebar fixed top-16 bottom-0 left-0 z-30 bg-[#0a192f] text-slate-300 flex flex-col border-r border-[#172a45] transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: 'var(--sidebar-width)' }}
      >
        {/* Navigation list */}
        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {sections.map((section, sIdx) => {
            // Filter items: must be active in Module Manager AND allowed by role
            const visibleItems = section.items.filter((item) => {
              if (item.code === 'module_manager') {
                return currentRole === 'ADMIN';
              }
              const active = isModuleActive(item.code);
              const allowed = hasPermission(item.code);
              return active && allowed;
            });

            if (visibleItems.length === 0) return null;

            return (
              <div key={sIdx} className="space-y-1">
                <div className="px-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                  {section.title}
                </div>

                <div className="mt-1 space-y-0.5">
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentRoute === item.route;

                    return (
                      <button
                        key={item.code}
                        onClick={() => {
                          onNavigate(item.route);
                          onCloseMobile();
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all group ${
                          isActive
                            ? 'bg-[#1b3a5b] text-white font-semibold shadow-xs'
                            : 'text-slate-300 hover:bg-[#112240] hover:text-white'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Icon
                            className={`w-4 h-4 shrink-0 transition-colors ${
                              isActive
                                ? 'text-amber-400'
                                : 'text-slate-400 group-hover:text-amber-300'
                            }`}
                          />
                          <span className="truncate">{item.name}</span>
                        </div>

                        {item.badge && (
                          <span
                            className={`text-[10px] font-bold px-1.5 py-0.2 rounded shrink-0 ml-1 ${
                              item.badgeColor || 'bg-blue-600/60 text-blue-200'
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer info */}
        <div className="p-3 border-t border-[#172a45] bg-[#071324] text-[11px] text-slate-400 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-slate-300 font-medium">PostgreSQL Connected</span>
          </div>
          <span className="text-slate-500 font-mono text-[10px]">v1.0.4</span>
        </div>
      </aside>
    </>
  );
};
